/**
 * Character Library Modal
 *
 * Modal component for managing uploaded rigged characters.
 * Users can upload PNG or JPEG images, view their library, and edit rigs.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import './CharacterLibrary.css';
import { CharacterRig, CharacterVoiceReference } from '../../types/RiggingTypes';
import {
  getAllRiggedCharacters,
  deleteRiggedCharacter,
  createNewCharacterRig,
  saveRiggedCharacter,
  isRigComplete,
  isCharacterDetailsComplete,
  withVoiceReference,
  withoutVoiceReference,
} from '../../services/riggedCharacterService';
import {
  getUploadURI,
  uploadCharacterImage,
  useCharacterImageUrl,
} from '../../services/characterStorageService';
import { isCharacterRiggingEnabled } from '../../services/featureFlagsService';
import {
  isImageAspectRatioWithinBounds,
  LIBRARY_IMAGE_ASPECT_BOUNDS,
} from '../../services/videoImageLimits';
import CharacterCard from './CharacterCard';
import RiggingEditor from './RiggingEditor';
import ActionInput from './ActionInput';
import AnimationRecorder from './AnimationRecorder';
import VoiceReference from './VoiceReference';
import VoiceReferenceUploader from './VoiceReferenceUploader';
import {
  Close,
  Upload,
  Add,
  AutoAwesome,
  Brush,
  Collections,
  EditNote,
} from '@mui/icons-material';

interface CharacterLibraryProps {
  isOpen: boolean;
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars
  onSelectCharacter: (rig: CharacterRig, actionId?: string) => void;
  /**
   * When provided, the studio jumps straight into the rig editor for that
   * character on open (used when handed off from the video generator).
   */
  focusCharacterId?: string;
}

type ViewMode =
  | 'library'
  | 'create'
  | 'details'
  | 'editor'
  | 'action'
  | 'recorder'
  | 'voice';

function isSupportedCharacterImage(file: File): boolean {
  const normalizedType = file.type.toLowerCase();
  const normalizedName = file.name.toLowerCase();

  return (
    normalizedType === 'image/png' ||
    normalizedType === 'image/jpeg' ||
    normalizedType === 'image/webp' ||
    normalizedName.endsWith('.png') ||
    normalizedName.endsWith('.jpg') ||
    normalizedName.endsWith('.jpeg') ||
    normalizedName.endsWith('.webp')
  );
}

/**
 * Reads an image file and resolves with its natural pixel dimensions. Used when
 * building the rig definition after the file has been persisted to storage.
 */
function readImageDimensions(
  file: File
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = () =>
        reject(new Error('Failed to read image dimensions'));
      img.src = dataUrl;
    };
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.readAsDataURL(file);
  });
}

const CharacterLibrary: React.FC<CharacterLibraryProps> = ({
  isOpen,
  onClose,
  onSelectCharacter,
  focusCharacterId,
}) => {
  const [characters, setCharacters] = useState<CharacterRig[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('library');
  const [selectedCharacter, setSelectedCharacter] =
    useState<CharacterRig | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [draftCharacterName, setDraftCharacterName] = useState('');
  const [draftCharacterDescription, setDraftCharacterDescription] = useState('');
  const [draftCharacterGender, setDraftCharacterGender] = useState('');
  const [draftCharacterSpecies, setDraftCharacterSpecies] = useState('');
  const [draftCharacterPersonality, setDraftCharacterPersonality] = useState('');
  const [draftVoiceReference, setDraftVoiceReference] =
    useState<CharacterVoiceReference | null>(null);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  // Signed preview URL for the character currently being edited in the
  // "details" subview (falls back to an empty string with no selection).
  const selectedImageSrc = useCharacterImageUrl(selectedCharacter?.imageUrl);

  // Load characters on mount
  useEffect(() => {
    if (isOpen) {
      loadCharacters();
    }
  }, [isOpen]);

  const loadCharacters = () => {
    const loaded = getAllRiggedCharacters();
    setCharacters(loaded);
  };

  const openUploadDialog = () => {
    uploadInputRef.current?.click();
  };

  const startCharacterCreation = () => {
    setDraftCharacterName('');
    setDraftCharacterDescription('');
    setDraftCharacterGender('');
    setDraftCharacterSpecies('');
    setDraftCharacterPersonality('');
    setSelectedCharacter(null);
    setViewMode('create');

    // Clear any previously attached voice draft.
    setDraftVoiceReference(null);
  };

  /**
   * Build an updated copy of an existing character from the draft identity
   * fields (and optionally a newly uploaded image). Voice changes are applied
   * through the shared `withVoiceReference`/`withoutVoiceReference` helpers.
   */
  const buildUpdatedRig = useCallback(
    (
      base: CharacterRig,
      image?: { url: string; name: string; width: number; height: number }
    ): CharacterRig => {
      const personality = draftCharacterPersonality
        .split(',')
        .map((trait) => trait.trim())
        .filter(Boolean);
      const name = draftCharacterName.trim() || base.name || base.imageName;
      const description = draftCharacterDescription.trim();

      let updated: CharacterRig = {
        ...base,
        name,
        description,
        tags: base.tags ?? base.identity?.tags ?? [],
        imageUrl: image?.url ?? base.imageUrl,
        imageName: image?.name ?? base.imageName,
        imageWidth: image?.width ?? base.imageWidth,
        imageHeight: image?.height ?? base.imageHeight,
        assets: image
          ? (base.assets ?? []).map((asset) =>
              asset.type === 'rigged_png' || asset.url === base.imageUrl
                ? { ...asset, name: image.name, url: image.url, isCanonical: true }
                : asset
            )
          : base.assets,
        identity: {
          name,
          description,
          species: draftCharacterSpecies.trim() || undefined,
          gender: draftCharacterGender.trim() || undefined,
          personality,
          biography: base.identity?.biography,
          tags: base.tags ?? base.identity?.tags ?? [],
        },
        updatedAt: new Date().toISOString(),
      };

      if (draftVoiceReference) {
        updated = withVoiceReference(updated, draftVoiceReference);
      } else if (base.voiceUrl) {
        updated = withoutVoiceReference(updated);
      }
      return updated;
    },
    [
      draftCharacterName,
      draftCharacterDescription,
      draftCharacterGender,
      draftCharacterSpecies,
      draftCharacterPersonality,
      draftVoiceReference,
    ]
  );

  const handleFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!isSupportedCharacterImage(file)) {
        event.target.value = '';
        alert('Please select a JPG, PNG, or WebP image.');
        return;
      }

      const token = localStorage.getItem('authToken');
      if (!token) {
        event.target.value = '';
        alert('Please log in to upload a character image.');
        return;
      }

      let dimensions: { width: number; height: number };
      try {
        dimensions = await readImageDimensions(file);
      } catch {
        event.target.value = '';
        alert('Failed to read image dimensions. Please try another image.');
        return;
      }

      const { width, height } = dimensions;
      if (
        !isImageAspectRatioWithinBounds(
          width,
          height,
          LIBRARY_IMAGE_ASPECT_BOUNDS
        )
      ) {
        event.target.value = '';
        alert(
          `Character images must have an aspect ratio between ${LIBRARY_IMAGE_ASPECT_BOUNDS.min} and ${LIBRARY_IMAGE_ASPECT_BOUNDS.max}. This image is ${(width / height).toFixed(3)}. Crop it and try again.`
        );
        return;
      }

      setIsUploading(true);

      try {
        // Request a signed upload URI for the image. No video id is passed, so
        // the backend stores the blob in the user's own storage directory:
        // `users/<userId>/assets/<fileName>`.
        const { uploadUrl, location } = await getUploadURI(file.name, token);
        await uploadCharacterImage(file, uploadUrl);

        // In "details" edit mode the newly uploaded image replaces the
        // character's visual source instead of creating a brand new character.
        if (viewMode === 'details' && selectedCharacter) {
          const updatedRig = buildUpdatedRig(selectedCharacter, {
            url: location,
            name: file.name,
            width,
            height,
          });
          await saveRiggedCharacter(updatedRig);
          loadCharacters();
          // Stay in the details editor with the refreshed character so further
          // identity edits can still be saved on top.
          setSelectedCharacter(updatedRig);
          return;
        }

        const newRig = createNewCharacterRig(
          location,
          draftCharacterName || file.name,
          width,
          height,
          {
            name: draftCharacterName,
            description: draftCharacterDescription,
            gender: draftCharacterGender,
            species: draftCharacterSpecies,
            personality: draftCharacterPersonality
              .split(',')
              .map((trait) => trait.trim())
              .filter(Boolean),
            // Attach the stored voice clip path (if any) to the new character
            // so voice is persisted with the character document from day one.
            voiceReference: draftVoiceReference ?? undefined,
          }
        );

        await saveRiggedCharacter(newRig);
        loadCharacters();

        if (isCharacterRiggingEnabled()) {
          // Open the rig editor for the new character so the rig can be set up.
          setSelectedCharacter(newRig);
          setViewMode('editor');
        } else {
          // Without rigging, return to the library so the new character can be
          // picked up in the video generator / voice flows.
          setSelectedCharacter(null);
          setViewMode('library');
        }
      } catch (error) {
        console.error('Failed to upload character image:', error);
        alert('Failed to upload character image. Please try again.');
      } finally {
        setIsUploading(false);
        event.target.value = '';
      }
    },
    [
      draftCharacterDescription,
      draftCharacterName,
      draftCharacterGender,
      draftCharacterSpecies,
      draftCharacterPersonality,
      draftVoiceReference,
      viewMode,
      selectedCharacter,
      buildUpdatedRig,
    ]
  );

  /**
   * Attach a freshly uploaded voice clip (from the shared voice uploader) to
   * the draft character. The clip has already been stored to base storage;
   * only the returned location is kept until the character is created.
   */
  const handleDraftVoiceUploaded = (voiceReference: CharacterVoiceReference) => {
    setDraftVoiceReference(voiceReference);
  };

  const removeDraftVoice = () => {
    setDraftVoiceReference(null);
  };

  /** Persist an updated voice reference from the VoiceReference subview. */
  const handleSaveVoice = async (updatedRig: CharacterRig) => {
    await saveRiggedCharacter(updatedRig);
    loadCharacters();
    // Stay in the voice subview so the newly attached clip is visible; the
    // parent renders the updated selected character.
    setSelectedCharacter(updatedRig);
  };

  const handleOpenVoice = (character: CharacterRig) => {
    setSelectedCharacter(character);
    setViewMode('voice');
  };

  const handleEditRig = (character: CharacterRig) => {
    setSelectedCharacter(character);
    setViewMode('editor');
  };

  /**
   * Open the details editor for an existing character, pre-filling the draft
   * identity fields and any attached voice clip so they can be edited and
   * saved back to the character.
   */
  const handleEditDetails = useCallback((character: CharacterRig) => {
    const voiceAsset = character.assets?.find(
      (asset) => asset.type === 'audio_reference'
    );
    setSelectedCharacter(character);
    setDraftCharacterName(character.name || character.identity?.name || '');
    setDraftCharacterDescription(
      character.description || character.identity?.description || ''
    );
    setDraftCharacterGender(character.identity?.gender || '');
    setDraftCharacterSpecies(character.identity?.species || '');
    setDraftCharacterPersonality(
      (character.identity?.personality ?? []).join(', ')
    );
    setDraftVoiceReference(
      character.voiceUrl
        ? {
            location: character.voiceUrl,
            fileName: voiceAsset?.name,
            durationSeconds:
              typeof voiceAsset?.metadata?.durationSeconds === 'number'
                ? voiceAsset.metadata.durationSeconds
                : undefined,
          }
        : null
    );
    setViewMode('details');
  }, []);

  // When handed off from the video generator, jump straight into the rig
  // editor (or the full character details editor when rigging is disabled)
  // for the focused character. Only handles each open once so that normal
  // studio navigation (library -> editor) is not overridden afterwards.
  const focusHandledRef = useRef(false);

  useEffect(() => {
    if (!isOpen || !focusCharacterId) {
      focusHandledRef.current = false;
      return;
    }
    if (focusHandledRef.current) return;

    const target = characters.find(
      (character) => character.id === focusCharacterId
    );
    if (target) {
      focusHandledRef.current = true;
      // The rig editor is a rigging feature. Without the flag we open the
      // full "Edit Character" view (identity, image, and voice) instead of
      // the voice-only subview.
      if (isCharacterRiggingEnabled()) {
        setSelectedCharacter(target);
        setViewMode('editor');
      } else {
        handleEditDetails(target);
      }
    }
  }, [isOpen, focusCharacterId, characters, handleEditDetails]);

  /** Persist the edited identity/voice (and optionally image) back to the rig. */
  const handleSaveDetails = async () => {
    if (!selectedCharacter) return;
    try {
      await saveRiggedCharacter(buildUpdatedRig(selectedCharacter));
      loadCharacters();
      setViewMode('library');
      setSelectedCharacter(null);
    } catch (error) {
      console.error('Failed to save character details:', error);
      alert('Failed to save character details. Please try again.');
    }
  };

  const handleDelete = (id: string) => {
    const deleteCharacter = async () => {
      await deleteRiggedCharacter(id);
      loadCharacters();
    };

    if (window.confirm('Are you sure you want to delete this character?')) {
      void deleteCharacter();
    }
  };

  const handleAddToCanvas = (character: CharacterRig) => {
    if (!isRigComplete(character)) {
      alert(
        'Please complete the rig setup before adding to canvas. Click "Edit Rig" to add joints and bones.'
      );
      return;
    }
    onSelectCharacter(character);
    onClose();
  };

  const handleApplyAction = (character: CharacterRig) => {
    setSelectedCharacter(character);
    setViewMode('action');
  };

  const handleRecordAnimation = (character: CharacterRig) => {
    if (!isRigComplete(character)) {
      alert(
        'Please complete the rig setup before recording. Click "Edit Rig" to add joints and bones.'
      );
      return;
    }
    setSelectedCharacter(character);
    setViewMode('recorder');
  };

  const handleRecordingSaved = () => {
    setViewMode('library');
    setSelectedCharacter(null);
  };

  const handleSaveRig = async (updatedRig: CharacterRig) => {
    await saveRiggedCharacter(updatedRig);
    loadCharacters();
    setViewMode('library');
    setSelectedCharacter(null);
  };

  const handleCancelEdit = () => {
    setViewMode('library');
    setSelectedCharacter(null);
  };

  const handleActionApplied = (character: CharacterRig, actionId: string) => {
    // When action is applied, add character to canvas with the action
    onSelectCharacter(character, actionId);
    onClose();
  };

  /**
   * Shared identity + voice form used by both the "create character" and
   * "edit character details" subviews. Bound directly to the draft state so
   * either panel can drive creation or updates.
   */
  const identityFields = (
    <>
      <div className="studio-field">
        <label htmlFor="character-name">
          Name{' '}
          <span
            className="studio-label-tip"
            title="If the name is pronounced differently than it is spelled, enter the phonetic spelling (e.g. Siobhan → Shivawn) so voices say it correctly."
            aria-label="If the name is pronounced differently than it is spelled, enter the phonetic spelling"
          >
            ⓘ
          </span>
        </label>
        <input
          id="character-name"
          type="text"
          value={draftCharacterName}
          onChange={(e) => setDraftCharacterName(e.target.value)}
          placeholder="e.g. Alex, Mira, Captain Nova"
          title="If the name is pronounced differently than it is spelled, enter the phonetic spelling (e.g. Siobhan → Shivawn) so voices say it correctly."
        />
      </div>

      <div className="studio-field">
        <label htmlFor="character-description">Description</label>
        <textarea
          id="character-description"
          value={draftCharacterDescription}
          onChange={(e) => setDraftCharacterDescription(e.target.value)}
          placeholder="A short note about who this character is."
        />
      </div>

      <div className="studio-field">
        <label htmlFor="character-gender">Gender</label>
        <input
          id="character-gender"
          type="text"
          value={draftCharacterGender}
          onChange={(e) => setDraftCharacterGender(e.target.value)}
          placeholder="e.g. male, female, non-binary"
        />
      </div>

      <div className="studio-field">
        <label htmlFor="character-species">Species</label>
        <input
          id="character-species"
          type="text"
          value={draftCharacterSpecies}
          onChange={(e) => setDraftCharacterSpecies(e.target.value)}
          placeholder="e.g. human, cat, robot"
        />
      </div>

      <div className="studio-field">
        <label htmlFor="character-personality">Personality</label>
        <input
          id="character-personality"
          type="text"
          value={draftCharacterPersonality}
          onChange={(e) => setDraftCharacterPersonality(e.target.value)}
          placeholder="Comma-separated traits, e.g. curious, kind"
        />
        <span className="studio-field-hint">
          Separate multiple traits with commas.
        </span>
      </div>

      <VoiceReferenceUploader
        voiceUrl={draftVoiceReference?.location ?? null}
        fileName={draftVoiceReference?.fileName}
        durationSeconds={draftVoiceReference?.durationSeconds}
        onUploaded={handleDraftVoiceUploaded}
        onRemove={removeDraftVoice}
        characterName={draftCharacterName || undefined}
      />
    </>
  );

  if (!isOpen) return null;

  const riggedCount = characters.filter((character) => isRigComplete(character)).length;
  const completedCount = characters.filter((character) =>
    isCharacterDetailsComplete(character)
  ).length;

  return (
    <div className="character-library-overlay" onClick={onClose}>
      <div
        className="character-library-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="character-library-header">
          <div className="character-library-header-copy">
            <span className="studio-kicker">Character Studio</span>
            <h2>
              {viewMode === 'library' && 'Character Studio'}
              {viewMode === 'create' && 'Create Character'}
              {viewMode === 'details' && 'Edit Character'}
              {viewMode === 'editor' && 'Rig Editor'}
              {viewMode === 'action' && 'Apply Action'}
              {viewMode === 'recorder' && 'Record Animation'}
              {viewMode === 'voice' && 'Voice Reference'}
            </h2>
          </div>
          <button className="close-button" onClick={onClose}>
            <Close />
          </button>
        </div>

        {/* Content */}
        <div className="character-library-content">
          {viewMode === 'library' && (
            <>
              {/* Studio Hero */}
              <section className="studio-hero">
                <div className="studio-hero-copy">
                  <span className="studio-hero-tag">Start here</span>
                  <h3>
                    {isCharacterRiggingEnabled()
                      ? 'Build a new character identity, then add visuals and rig later.'
                      : 'Build a new character identity, then add visuals and voice.'}
                  </h3>
                  <p>
                    Character Studio is centered on persistent identity.
                    Start by naming your character, then add a visual source,{' '}
                    {isCharacterRiggingEnabled()
                      ? 'optional rigs, and future assets'
                      : 'a voice reference, and future assets'}{' '}
                    over time.
                  </p>

                  <div className="studio-hero-actions">
                    <button
                      className="studio-action primary"
                      onClick={startCharacterCreation}
                    >
                      <Brush style={{ fontSize: 18 }} />
                      Start from scratch
                    </button>
                  </div>
                </div>

                <div className="studio-hero-stats">
                  <div className="studio-stat">
                    <span>{characters.length}</span>
                    <label>
                      {isCharacterRiggingEnabled() ? 'Saved rigs' : 'Characters'}
                    </label>
                  </div>
                  {isCharacterRiggingEnabled() && (
                    <>
                      <div className="studio-stat">
                        <span>{riggedCount}</span>
                        <label>Ready to animate</label>
                      </div>
                      <div className="studio-stat">
                        <span>{characters.length - riggedCount}</span>
                        <label>Still in progress</label>
                      </div>
                    </>
                  )}
                  <div className="studio-stat">
                    <span>{completedCount}</span>
                    <label>Completed details</label>
                  </div>
                </div>
              </section>

              <div className="studio-section-header">
                <div>
                  <span className="studio-section-eyebrow">Your library</span>
                  <h3>Open and refine existing characters</h3>
                </div>
                <div className="studio-section-note">
                  <AutoAwesome style={{ fontSize: 16 }} />
                  Enhance existing characters, then use them to generate videos whenever you are
                  ready.
                </div>
              </div>

              {/* Character Grid */}
              <div className="character-grid">
                {characters.length === 0 ? (
                  <div className="empty-state">
                    <Collections style={{ fontSize: 48, opacity: 0.5 }} />
                    <p>No characters in the studio yet</p>
                    <p>
                      Start by naming a character, then attach a visual source
                      or generate one later.
                    </p>
                    <div className="empty-state-actions">
                      <button
                        className="studio-action primary"
                        onClick={startCharacterCreation}
                      >
                        <Add style={{ fontSize: 18 }} />
                        Start from scratch
                      </button>
                    </div>
                  </div>
                ) : (
                  characters.map((character) => (
                    <CharacterCard
                      key={character.id}
                      character={character}
                      onEditDetails={() => handleEditDetails(character)}
                      onEdit={() => handleEditRig(character)}
                      onDelete={() => handleDelete(character.id)}
                      onAddToCanvas={() => handleAddToCanvas(character)}
                      onApplyAction={() => handleApplyAction(character)}
                      onRecordAnimation={() => handleRecordAnimation(character)}
                      onVoice={() => handleOpenVoice(character)}
                    />
                  ))
                )}
              </div>
            </>
          )}

          {viewMode === 'create' && (
            <div className="studio-create-shell">
              <input
                ref={uploadInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                onChange={handleFileUpload}
                disabled={isUploading}
                style={{ display: 'none' }}
              />

              <div className="studio-create-grid">
                <section className="studio-create-panel">
                  <div className="studio-create-panel-header">
                    <span className="studio-section-eyebrow">
                      Character Identity
                    </span>
                    <h3>Name your actor first</h3>
                    <p>
                      This name will stay with the character as you add new
                      assets, voice, and motion later.
                    </p>
                  </div>

                  {identityFields}

                  <div className="studio-create-actions">
                    <button
                      className="studio-action primary"
                      onClick={openUploadDialog}
                      disabled={isUploading}
                    >
                      <Upload style={{ fontSize: 18 }} />
                      {isUploading
                        ? 'Uploading...'
                        : 'Upload a character image to begin'}
                    </button>
                    <button
                      className="studio-action secondary"
                      disabled
                      title="Coming soon"
                    >
                      <AutoAwesome style={{ fontSize: 18 }} />
                      Generate with image model
                    </button>
                  </div>
                </section>

                <section className="studio-create-panel studio-create-panel-muted">
                  <span className="studio-section-eyebrow">Next up</span>
                  <h3>What happens after upload</h3>
                  <ul className="studio-create-list">
                    <li>Create the character with your chosen name.</li>
                    {isCharacterRiggingEnabled() && (
                      <li>
                        Open the rig editor only if you want skeletal animation.
                      </li>
                    )}
                    <li>Add more assets later without changing the identity.</li>
                  </ul>
                  <button
                    className="studio-back-button"
                    onClick={() => setViewMode('library')}
                  >
                    <EditNote style={{ fontSize: 18 }} />
                    Back to studio
                  </button>
                </section>
              </div>
            </div>
          )}

          {viewMode === 'details' && selectedCharacter && (
            <div className="studio-subview-shell">
              <button className="studio-back-button" onClick={handleCancelEdit}>
                <EditNote style={{ fontSize: 18 }} />
                Back to studio
              </button>

              <div className="studio-create-shell">
                <input
                  ref={uploadInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                  onChange={handleFileUpload}
                  disabled={isUploading}
                  style={{ display: 'none' }}
                />

                <div className="studio-create-grid">
                  <section className="studio-create-panel">
                    <div className="studio-create-panel-header">
                      <span className="studio-section-eyebrow">
                        Character Identity
                      </span>
                      <h3>
                        Edit{' '}
                        {selectedCharacter.name ||
                          selectedCharacter.identity?.name ||
                          selectedCharacter.imageName}
                      </h3>
                      <p>
                        Update the details below, swap the visual source, or
                        manage the voice reference.
                      </p>
                    </div>

                    {selectedImageSrc && (
                      <div className="studio-create-image-preview">
                        <img
                          src={selectedImageSrc}
                          alt={selectedCharacter.imageName}
                        />
                      </div>
                    )}

                    {identityFields}

                    <div className="studio-create-actions">
                      <button
                        className="studio-action secondary"
                        onClick={openUploadDialog}
                        disabled={isUploading}
                        title="Replace the character image"
                      >
                        <Upload style={{ fontSize: 18 }} />
                        {isUploading ? 'Uploading...' : 'Replace image'}
                      </button>

                      <button
                        className="studio-action primary"
                        onClick={handleSaveDetails}
                        disabled={isUploading}
                      >
                        <EditNote style={{ fontSize: 18 }} />
                        Save changes
                      </button>
                    </div>
                  </section>

                  <section className="studio-create-panel studio-create-panel-muted">
                    <span className="studio-section-eyebrow">
                      Using this character
                    </span>
                    <h3>Ready for video</h3>
                    <ul className="studio-create-list">
                      <li>
                        Details and voice are used when generating character
                        videos.
                      </li>
                      <li>
                        Swap the image at any time without losing the identity.
                      </li>
                      {isCharacterRiggingEnabled() && (
                        <li>
                          Open Studio to set up the rig for canvas animation.
                        </li>
                      )}
                    </ul>
                    <button
                      className="studio-back-button"
                      onClick={() => setViewMode('library')}
                    >
                      <EditNote style={{ fontSize: 18 }} />
                      Back to studio
                    </button>
                  </section>
                </div>
              </div>
            </div>
          )}

          {isCharacterRiggingEnabled() && viewMode === 'editor' && selectedCharacter && (
            <div className="studio-subview-shell">
              <button className="studio-back-button" onClick={handleCancelEdit}>
                <EditNote style={{ fontSize: 18 }} />
                Back to studio
              </button>
              <RiggingEditor
                character={selectedCharacter}
                onSave={handleSaveRig}
                onCancel={handleCancelEdit}
              />
            </div>
          )}

          {isCharacterRiggingEnabled() && viewMode === 'action' && selectedCharacter && (
            <div className="studio-subview-shell">
              <button className="studio-back-button" onClick={handleCancelEdit}>
                <EditNote style={{ fontSize: 18 }} />
                Back to studio
              </button>
              <ActionInput
                character={selectedCharacter}
                onApply={handleActionApplied}
                onCancel={handleCancelEdit}
              />
            </div>
          )}

          {isCharacterRiggingEnabled() && viewMode === 'recorder' && selectedCharacter && (
            <div className="studio-subview-shell">
              <button className="studio-back-button" onClick={handleCancelEdit}>
                <EditNote style={{ fontSize: 18 }} />
                Back to studio
              </button>
              <AnimationRecorder
                character={selectedCharacter}
                onSave={handleRecordingSaved}
                onCancel={handleCancelEdit}
              />
            </div>
          )}

          {viewMode === 'voice' && selectedCharacter && (
            <div className="studio-subview-shell">
              <button className="studio-back-button" onClick={handleCancelEdit}>
                <EditNote style={{ fontSize: 18 }} />
                Back to studio
              </button>
              <VoiceReference
                character={selectedCharacter}
                onSave={handleSaveVoice}
                onCancel={handleCancelEdit}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CharacterLibrary;
